import be from "@arcgis/core/identity/IdentityManager";
import zr from "@arcgis/core/identity/OAuthInfo";
import H from "@arcgis/core/portal/Portal";
import { createLogger as x } from "@origo/logger";
import ir from "@arcgis/core/portal/PortalQueryParams";
import Ee from "@arcgis/core/portal/PortalItem";
import Te from "@arcgis/core/request";
import Qr from "@arcgis/core/rest/support/Query";
import * as Xr from "@arcgis/core/rest/query";
import Z, { useEffect as j, useRef as xe, useCallback as Hr, useState as F, Component as Zr } from "react";
import { useDispatch as Ae, useSelector as $ } from "react-redux";
import { createAsyncThunk as W, createSlice as Oe, combineReducers as et } from "@reduxjs/toolkit";
import { CalciteAlert as rt, CalciteButton as tt } from "@esri/calcite-components-react";
const N = x("AuthService");
class ar {
  constructor(r) {
    this.config = {
      appId: r.appId,
      portalUrl: r.portalUrl || "https://www.arcgis.com",
      flowType: r.flowType || "auto",
      popup: r.popup ?? !1
    };
  }
  /**
   * Initializes OAuth configuration and registers with IdentityManager
   * 
   * @example
   * ```typescript
   * const authService = new AuthService({
   *   appId: '06bJzGl3cUQAR5lX',
   *   portalUrl: 'https://www.arcgis.com'
   * });
   * authService.initialize();
   * ```
   */
  initialize() {
    if (this.oauthInfo) {
      N.debug("Already initialized");
      return;
    }
    this.oauthInfo = new zr({
      appId: this.config.appId,
      portalUrl: this.config.portalUrl,
      flowType: this.config.flowType,
      popup: this.config.popup
    }), be.registerOAuthInfos([this.oauthInfo]), N.debug(`Initialized OAuth for ${this.config.portalUrl}`);
  }
  /**
   * Checks current sign-in status and retrieves user information
   * 
   * @param requireAuth - If true, forces sign-in if not authenticated
   * @returns Object with sign-in status and user info
   * 
   * @example
   * ```typescript
   * // Check status without forcing sign-in (for public apps)
   * const { isSignedIn, user } = await authService.checkSignInStatus(false);
   * 
   * // Force sign-in (for authenticated apps)
   * const { isSignedIn, user } = await authService.checkSignInStatus(true);
   * ```
   */
  async checkSignInStatus(r = !1) {
    try {
      const t = new H({ url: this.config.portalUrl });
      if (t.authMode = r ? "immediate" : "auto", await t.load(), t.user) {
        const i = {
          username: t.user.username,
          fullName: t.user.fullName || void 0,
          email: t.user.email || void 0,
          role: t.user.role || void 0,
          thumbnail: t.user.thumbnailUrl || void 0,
          orgUrl: t.url || void 0
        };
        return N.info(`User signed in: ${i.username}`), { isSignedIn: !0, user: i };
      }
      return N.debug("User not signed in"), { isSignedIn: !1 };
    } catch (t) {
      if (N.error("Error checking sign-in status:", t), r)
        throw new Error("Authentication required but sign-in failed");
      return { isSignedIn: !1 };
    }
  }
  /**
   * Forces user sign-in
   * 
   * Used by authenticated apps (e.g., drømmeplan-admin) to require authentication.
   * Will trigger OAuth flow if user is not signed in.
   * 
   * @returns User information after successful sign-in
   * @throws Error if sign-in fails
   * 
   * @example
   * ```typescript
   * try {
   *   const user = await authService.requireSignIn();
   *   console.log('Signed in as:', user.username);
   * } catch (error) {
   *   console.error('Sign-in failed:', error);
   * }
   * ```
   */
  async requireSignIn() {
    const r = await this.checkSignInStatus(!0);
    if (!r.isSignedIn || !r.user)
      throw new Error("Sign-in required but user not authenticated");
    return r.user;
  }
  /**
   * Signs out current user
   * 
   * @example
   * ```typescript
   * await authService.signOut();
   * ```
   */
  async signOut() {
    try {
      await be.destroyCredentials(), N.debug("User signed out");
    } catch (r) {
      throw N.error("Error signing out:", r), r;
    }
  }
  /**
   * Checks whether the signed-in user is a member of a Portal group
   *
   * @param groupIdOrTitle - AGOL group id or title to match against
   * @returns True if the user belongs to a group matching the id or title
   */
  async isMemberOfGroup(r) {
    const i = await (await this.getAuthenticatedPortal()).user.fetchGroups(), o = r.trim().toLowerCase();
    return (i || []).some(
      (p) => p.id === r || (p.title || "").trim().toLowerCase() === o
    );
  }
  /**
   * Gets current portal instance
   *
   * @returns Portal instance configured for this auth service
   */
  getPortal() {
    return new H({ url: this.config.portalUrl });
  }
  /**
   * Gets an authenticated and loaded Portal instance with user context.
   * 
   * Caches the loaded portal for subsequent calls to avoid repeated auth round-trips.
   * Requires that IdentityManager has been initialized with OAuth credentials
   * (i.e., the user has signed in via `checkSignInStatus` or `requireSignIn` first).
   * 
   * @returns Loaded Portal instance with `portal.user` available
   * @throws Error if portal cannot be loaded or user is not authenticated
   * 
   * @example
   * ```typescript
   * const portal = await authService.getAuthenticatedPortal();
   * console.log('Authenticated as:', portal.user.username);
   * ```
   */
  async getAuthenticatedPortal() {
    if (this.cachedPortal?.user)
      return this.cachedPortal;
    const r = new H({ url: this.config.portalUrl });
    if (r.authMode = "immediate", await r.load(), !r.user)
      throw new Error("Portal loaded but no authenticated user found");
    return this.cachedPortal = r, r;
  }
}
class _ extends Error {
  constructor(r, t, i) {
    super(r), this.name = "SettingsError", this.errorType = t, Object.assign(this, i);
  }
  /**
   * Creates user-friendly Norwegian error message
   */
  toUserMessage() {
    switch (this.errorType) {
      case "NOT_FOUND":
        return `Kunne ikke finne innstillinger for ${this.itemName || "applikasjonen"}. Kontakt administrator.`;
      case "NETWORK":
        return "Kunne ikke laste innstillinger på grunn av nettverksfeil. Prøv igjen.";
      case "PARSE":
        return "Innstillingene har ugyldig format. Kontakt administrator.";
      case "UNAUTHORIZED":
        return "Du har ikke tilgang til å laste innstillinger. Kontakt administrator.";
      default:
        return "En ukjent feil oppstod ved lasting av innstillinger. Kontakt administrator.";
    }
  }
}
const ae = x("Auth");
let K = null;
function Ie() {
  if (K !== null)
    return K;
  const n = window.location.hostname;
  if (n === "localhost" || n === "127.0.0.1") {
    const t = typeof import.meta < "u" && { BASE_URL: "/", MODE: "production", DEV: !1, PROD: !0, SSR: !1 }.VITE_DEV_CUSTOMER, i = t || "mars";
    return t ? ae.debug(`Development mode: using VITE_DEV_CUSTOMER='${i}'`) : ae.debug(`Development mode: using default customer '${i}'. Set VITE_DEV_CUSTOMER to override.`), K = i, i;
  }
  const r = n.split(".");
  if (r.length >= 2) {
    const t = r[1];
    return ae.debug(`Extracted customer domain from hostname: '${t}'`), K = t, t;
  }
  return ae.warn(`Unexpected hostname format: '${n}'. Using hostname as customer domain.`), K = n, n;
}
function nt(n) {
  const r = Ie();
  return `title:"${n}.tokenservice", tags:"${n}.tokenservice@${r}"`;
}
function st(n) {
  const r = Ie();
  return `title:"${n}.settings", tags:"${n}.settings@${r}"`;
}
const P = x("SettingsService");
class or {
  constructor(r = {}) {
    this.portalUrl = r.portalUrl || "https://www.arcgis.com", this.timeout = r.timeout || 1e4;
  }
  /**
   * Loads app settings from portal item
   * 
   * @param config - Settings configuration
   * @param requireAuth - Whether authentication is required for loading settings
   * @returns Loaded settings
   * @throws SettingsError if the settings item cannot be found, accessed, or parsed
   *
   * @example
   * ```typescript
   * const settingsService = new SettingsService();
   *
   * try {
   *   const settings = await settingsService.loadSettings({
   *     appName: 'drømmeplan',
   *     defaultSettings: { webmap: 'default-id' }
   *   }, false);
   * } catch (error) {
   *   if (error instanceof SettingsError) {
   *     alert(error.toUserMessage());
   *   }
   * }
   * ```
   */
  async loadSettings(r, t = !1) {
    const i = st(r.appName), o = Ie();
    P.debug(`Loading settings: ${i}`);
    let p;
    try {
      const d = new H({ url: this.portalUrl });
      d.authMode = t ? "immediate" : "anonymous", await d.load();
      const c = new ir({
        query: i
      }), g = new Promise((w, O) => {
        p = setTimeout(() => {
          O(new _(
            `Settings query timed out after ${this.timeout}ms`,
            "NETWORK",
            {
              itemName: i,
              customer: o,
              portalUrl: this.portalUrl,
              technicalDetails: `Query timeout: ${this.timeout}ms`
            }
          ));
        }, this.timeout);
      }), S = await Promise.race([
        d.queryItems(c),
        g
      ]);
      clearTimeout(p);
      const v = S.results;
      if (v.length === 0)
        throw P.warn(`Settings item not found: ${i}`), new _(
          `Settings item not found: ${i}`,
          "NOT_FOUND",
          { itemName: i, customer: o, portalUrl: this.portalUrl }
        );
      const y = v[0];
      P.debug(`Found settings item: ${y.id}`);
      try {
        const w = await y.fetchData();
        if (!w || typeof w != "object")
          throw P.warn(`Settings data has invalid format. Expected object, got ${typeof w}`), new _(
            `Settings data has invalid format for ${i}`,
            "PARSE",
            { itemName: i, customer: o, portalUrl: this.portalUrl }
          );
        return P.debug("Settings loaded successfully"), w;
      } catch (w) {
        throw w instanceof _ ? w : (P.warn("Failed to fetch settings data from item:", w), new _(
          `Failed to fetch settings data for ${i}`,
          "PARSE",
          {
            itemName: i,
            customer: o,
            portalUrl: this.portalUrl,
            technicalDetails: w instanceof Error ? w.message : String(w)
          }
        ));
      }
    } catch (d) {
      throw clearTimeout(p), d instanceof _ ? (P.warn("Failed to load settings:", d), d) : (P.warn("Error loading settings:", d), new _(
        `Unexpected error loading settings: ${i}`,
        "UNKNOWN",
        {
          itemName: i,
          customer: o,
          portalUrl: this.portalUrl,
          technicalDetails: d instanceof Error ? d.message : String(d)
        }
      ));
    }
  }
}
const J = x("PortalItemService");
class Ft {
  constructor(r) {
    this.portal = r;
  }
  /**
   * Creates a new Portal item with JSON data
   * 
   * @param data - JSON-serializable data to store
   * @param metadata - Item title, tags, and optional description
   * @returns The ID of the created item
   */
  async createItem(r, t) {
    const i = new Ee({
      portal: this.portal,
      title: t.title,
      type: t.type || "Web Mapping Application",
      tags: t.tags,
      snippet: t.snippet,
      description: t.description,
      typeKeywords: ["origo", "filter-template"]
    });
    if (!this.portal.user)
      throw new Error("No authenticated user on portal");
    const o = await this.portal.user.addItem({
      item: i,
      data: JSON.stringify(r)
    });
    if (!o?.id)
      throw new Error("Failed to create Portal item - no ID returned");
    return J.debug(`Created item: ${o.id}`), o.id;
  }
  /**
   * Updates an existing Portal item's data and metadata
   * 
   * @param itemId - ID of the item to update
   * @param data - New JSON data
   * @param metadata - Optional updated metadata
   */
  async updateItem(r, t, i) {
    const o = new Ee({
      portal: this.portal,
      id: r
    });
    await o.load(), i?.title && (o.title = i.title), i?.snippet && (o.snippet = i.snippet), i?.description && (o.description = i.description), i?.tags && (o.tags = i.tags), await o.update({ data: JSON.stringify(t) }), J.debug(`Updated item: ${r}`);
  }
  /**
   * Deletes a Portal item
   * 
   * @param itemId - ID of the item to delete
   */
  async deleteItem(r) {
    if (!this.portal.user)
      throw new Error("No authenticated user on portal");
    const t = `${this.portal.restUrl}/content/users/${this.portal.user.username}/items/${r}/delete`;
    await Te(t, {
      method: "post",
      query: { f: "json" },
      authMode: "immediate"
    }), J.debug(`Deleted item: ${r}`);
  }
  /**
   * Queries Portal items by tags. Returns own items and items shared
   * with the user's groups.
   * 
   * @param tags - Tags to filter by (all must match)
   * @returns Array of item results with their data
   */
  async queryItems(r) {
    const i = `${r.map((c) => `tags:"${c}"`).join(" AND ")} AND type:"Web Mapping Application"`, o = await this.portal.queryItems({
      query: i,
      num: 100,
      sortField: "modified",
      sortOrder: "desc"
    }), p = await Promise.allSettled(
      o.results.map(async (c) => {
        const g = await c.fetchData();
        return {
          id: c.id,
          title: c.title,
          snippet: c.snippet || void 0,
          owner: c.owner,
          created: c.created?.getTime() ?? 0,
          modified: c.modified?.getTime() ?? 0,
          tags: c.tags || [],
          data: g
        };
      })
    ), d = [];
    for (const c of p)
      c.status === "fulfilled" ? d.push(c.value) : J.warn("Failed to fetch data for an item:", c.reason);
    return d;
  }
  /**
   * Shares a Portal item with specified groups
   * 
   * @param itemId - ID of the item to share
   * @param groupIds - Array of group IDs to share with
   */
  async shareToGroups(r, t) {
    if (await new Ee({
      portal: this.portal,
      id: r
    }).load(), !this.portal.user)
      throw new Error("No authenticated user on portal");
    const o = `${this.portal.restUrl}/content/users/${this.portal.user.username}/items/${r}/share`;
    await Te(o, {
      method: "post",
      query: {
        everyone: !1,
        org: !1,
        groups: t.join(","),
        f: "json"
      },
      authMode: "immediate"
    }), J.debug(`Shared item ${r} with groups: ${t.join(", ")}`);
  }
  /**
   * Gets the authenticated user's Portal groups, useful for showing
   * sharing options in the UI.
   * 
   * @returns Array of groups with id and title
   */
  async getUserGroups() {
    const r = this.portal.user;
    if (!r)
      throw new Error("No authenticated user on portal");
    return (await r.fetchGroups() || []).map((i) => ({
      id: i.id || "",
      title: i.title || ""
    }));
  }
}
const tr = x("GdoTokenProvider");
class ur {
  constructor(r, t = {}) {
    this.refreshCallbacks = [], this.isRefreshing = !1, this.appName = r, this.requireAuth = t.requireAuth ?? !1, this.portalUrl = t.portalUrl || "https://www.arcgis.com", this.refreshConfig = {
      refreshBufferMinutes: t.refreshConfig?.refreshBufferMinutes ?? 5,
      maxRetries: t.refreshConfig?.maxRetries ?? 3,
      retryBaseDelay: t.refreshConfig?.retryBaseDelay ?? 30
    };
  }
  async getToken() {
    if (this.currentToken && !this.isExpired())
      return this.currentToken;
    try {
      return await this.fetchTokenFromService();
    } catch (r) {
      tr.warn("Failed:", r);
      return;
    }
  }
  async refreshToken() {
    if (this.isRefreshing)
      return new Promise((r, t) => {
        const i = setTimeout(() => {
          t(new Error("Token refresh timed out waiting for in-flight refresh"));
        }, 3e4);
        this.refreshCallbacks.push((o) => {
          clearTimeout(i), o ? r(o) : t(new Error("Token refresh failed"));
        });
      });
    this.isRefreshing = !0;
    try {
      const r = await this.fetchTokenFromService();
      return this.isRefreshing = !1, this.refreshCallbacks.forEach((t) => t(r)), r;
    } catch (r) {
      throw this.isRefreshing = !1, this.refreshCallbacks.forEach((t) => t(void 0)), r;
    }
  }
  getExpiration() {
    return this.expiresAt;
  }
  isExpired() {
    if (!this.expiresAt)
      return !1;
    const r = Date.now(), t = this.refreshConfig.refreshBufferMinutes * 60 * 1e3;
    return r >= this.expiresAt - t;
  }
  onTokenRefreshed(r) {
    this.refreshCallbacks.push(r);
  }
  cleanup() {
    this.refreshTimer && (clearTimeout(this.refreshTimer), this.refreshTimer = void 0), this.refreshCallbacks = [];
  }
  async fetchTokenFromService() {
    const r = nt(this.appName), t = new ir({ query: r }), i = new H({ url: this.portalUrl });
    this.requireAuth || (i.authMode = "anonymous");
    const p = (await i.queryItems(t)).results;
    if (p.length === 0)
      throw new Error("Token service not found");
    const d = new Qr({ where: "1=1", outFields: ["*"], returnGeometry: !1 }), c = await Xr.executeQueryJSON(p[0].url, d);
    if (c.features.length === 0)
      throw new Error("No token in service");
    const g = c.features[0].attributes, S = g.token;
    if (!S)
      throw new Error("Token attribute empty");
    this.currentToken = S;
    const v = g.expires || g.expiresAt || g.expiration;
    return v ? (this.expiresAt = typeof v == "number" ? v : Date.parse(v), this.scheduleRefresh()) : (this.expiresAt = Date.now() + 24 * 60 * 60 * 1e3, this.scheduleRefresh()), S;
  }
  scheduleRefresh() {
    if (this.refreshTimer && clearTimeout(this.refreshTimer), !this.expiresAt)
      return;
    const r = Date.now(), t = this.refreshConfig.refreshBufferMinutes * 60 * 1e3, i = this.expiresAt - t - r;
    if (i <= 0) {
      this.refreshWithRetry();
      return;
    }
    this.refreshTimer = window.setTimeout(() => this.refreshWithRetry(), i);
  }
  async refreshWithRetry(r = 1) {
    try {
      await this.refreshToken();
    } catch {
      if (r < this.refreshConfig.maxRetries) {
        const i = this.refreshConfig.retryBaseDelay * Math.pow(2, r - 1);
        setTimeout(() => this.refreshWithRetry(r + 1), i * 1e3);
      } else
        tr.error("Token refresh failed after max retries");
    }
  }
  async queryRoles(r) {
    try {
      return (await Te("https://services.geodataonline.no/arcgis/rest/services/ProxyServices/roles", { query: { token: r } })).data;
    } catch {
      return {};
    }
  }
  async getTokenMetadata() {
    const r = await this.getToken();
    if (!r)
      return;
    const t = await this.queryRoles(r);
    return { token: r, expiresAt: this.expiresAt, username: t.username, customerName: t.customerName, applicationRoles: t.applicationRoles };
  }
}
const it = {
  isSignedIn: !1,
  user: void 0,
  status: "idle",
  error: void 0,
  isAuthRequired: !1
}, oe = W(
  "auth/checkSignInStatus",
  async ({ authService: n, requireAuth: r }, { rejectWithValue: t }) => {
    try {
      return await n.checkSignInStatus(r);
    } catch (i) {
      return t(i.message);
    }
  }
), ue = W(
  "auth/requireSignIn",
  async ({ authService: n }, { rejectWithValue: r }) => {
    try {
      return await n.requireSignIn();
    } catch (t) {
      return r(t.message);
    }
  }
), le = W(
  "auth/signOut",
  async ({ authService: n }, { rejectWithValue: r }) => {
    try {
      await n.signOut();
    } catch (t) {
      return r(t.message);
    }
  }
), lr = Oe({
  name: "auth",
  initialState: it,
  reducers: {
    setIsAuthRequired: (n, r) => {
      n.isAuthRequired = r.payload;
    },
    clearAuth: (n) => {
      n.isSignedIn = !1, n.user = void 0, n.status = "idle", n.error = void 0;
    }
  },
  extraReducers: (n) => {
    n.addCase(oe.pending, (r) => {
      r.status = "loading", r.error = void 0;
    }).addCase(oe.fulfilled, (r, t) => {
      r.status = "succeeded", r.isSignedIn = t.payload.isSignedIn, r.user = t.payload.user;
    }).addCase(oe.rejected, (r, t) => {
      r.status = "failed", r.error = t.payload;
    }).addCase(ue.pending, (r) => {
      r.status = "loading", r.error = void 0;
    }).addCase(ue.fulfilled, (r, t) => {
      r.status = "succeeded", r.isSignedIn = !0, r.user = t.payload;
    }).addCase(ue.rejected, (r, t) => {
      r.status = "failed", r.error = t.payload;
    }).addCase(le.pending, (r) => {
      r.status = "loading";
    }).addCase(le.fulfilled, (r) => {
      r.status = "idle", r.isSignedIn = !1, r.user = void 0, r.error = void 0;
    }).addCase(le.rejected, (r, t) => {
      r.status = "failed", r.error = t.payload;
    });
  }
}), { setIsAuthRequired: Mt, clearAuth: qt } = lr.actions, at = lr.reducer, ot = (n) => n.auth.auth.isSignedIn, ut = (n) => n.auth.auth.user, lt = (n) => n.auth.auth.status, Wt = (n) => n.auth.auth.error, Lt = (n) => n.auth.auth.isAuthRequired;
function cr(n, r = !1) {
  const t = Ae(), i = $(ot), o = $(ut), p = $(lt);
  return j(() => {
    t(oe({ authService: n, requireAuth: r }));
  }, [t, n, r]), { isSignedIn: i, user: o, isLoading: p === "loading", signIn: async () => {
    await t(ue({ authService: n })).unwrap();
  }, signOut: async () => {
    await t(le({ authService: n })).unwrap(), window.location.reload();
  } };
}
const fr = x("tokenSlice"), ct = {
  token: void 0,
  expiresAt: void 0,
  isRefreshing: !1,
  lastRefreshError: void 0,
  appName: void 0,
  username: void 0,
  customerName: void 0,
  applicationRoles: []
}, ce = W("token/getToken", async ({ tokenProvider: n }) => {
  try {
    return await n.getTokenMetadata?.();
  } catch (r) {
    fr.warn("Failed to get token:", r);
    return;
  }
}), ke = W(
  "token/refreshToken",
  async ({ tokenProvider: n }) => {
    try {
      return await n.refreshToken(), await n.getTokenMetadata?.();
    } catch (r) {
      fr.warn("Failed to refresh token:", r);
      return;
    }
  }
), dr = Oe({
  name: "token",
  initialState: ct,
  reducers: {
    setToken: (n, r) => {
      n.token = r.payload;
    },
    setTokenMetadata: (n, r) => {
      r.payload.token && (n.token = r.payload.token), r.payload.expiresAt && (n.expiresAt = r.payload.expiresAt), r.payload.username && (n.username = r.payload.username), r.payload.customerName && (n.customerName = r.payload.customerName), r.payload.applicationRoles && (n.applicationRoles = r.payload.applicationRoles);
    },
    setAppName: (n, r) => {
      n.appName = r.payload;
    },
    clearToken: (n) => {
      n.token = void 0, n.expiresAt = void 0, n.lastRefreshError = void 0, n.username = void 0, n.customerName = void 0, n.applicationRoles = [];
    }
  },
  extraReducers: (n) => {
    n.addCase(ce.pending, (r) => {
      r.isRefreshing = !0, r.lastRefreshError = void 0;
    }).addCase(ce.fulfilled, (r, t) => {
      r.isRefreshing = !1, t.payload && (r.token = t.payload.token, r.expiresAt = t.payload.expiresAt, r.username = t.payload.username, r.customerName = t.payload.customerName, r.applicationRoles = t.payload.applicationRoles || []);
    }).addCase(ce.rejected, (r, t) => {
      r.isRefreshing = !1, r.lastRefreshError = t.error?.message;
    }).addCase(ke.pending, (r) => {
      r.isRefreshing = !0, r.lastRefreshError = void 0;
    }).addCase(ke.fulfilled, (r, t) => {
      r.isRefreshing = !1, t.payload && (r.token = t.payload.token, r.expiresAt = t.payload.expiresAt, r.username = t.payload.username, r.customerName = t.payload.customerName, r.applicationRoles = t.payload.applicationRoles || []);
    }).addCase(ke.rejected, (r, t) => {
      r.isRefreshing = !1, r.lastRefreshError = t.error?.message;
    });
  }
}), { setToken: Vt, setTokenMetadata: ft, setAppName: Yt, clearToken: Gt } = dr.actions, dt = dr.reducer, ht = (n) => n.auth.token.token, Bt = (n) => n.auth.token.expiresAt, Kt = (n) => n.auth.token.expiresAt ? Date.now() >= n.auth.token.expiresAt : !1, pt = (n) => n.auth.token.isRefreshing, Jt = (n) => n.auth.token.lastRefreshError, zt = (n) => n.auth.token.token, Qt = (n) => n.auth.token.applicationRoles, Xt = (n) => n.auth.token.username, Ht = (n) => n.auth.token.customerName, gt = x("useTokenProvider");
function hr(n) {
  const r = Ae(), t = $(ht), i = $(pt), o = xe(!1);
  j(() => (o.current || (o.current = !0, r(ce({ tokenProvider: n })), n.onTokenRefreshed((d) => {
    r(ft({ token: d, expiresAt: n.getExpiration() }));
  })), () => {
    n.cleanup();
  }), [r, n]);
  const p = Hr(async () => {
    try {
      await n.refreshToken();
    } catch (d) {
      gt.warn("Token refresh failed:", d);
    }
  }, [n]);
  return { token: t, isRefreshing: i, refresh: p };
}
const mt = x("settingsSlice"), vt = {
  settings: void 0,
  isLoading: !1,
  error: void 0
}, Ce = W(
  "settings/loadSettings",
  async ({ settingsService: n, config: r, requireAuth: t }) => {
    try {
      return { settings: await n.loadSettings(r, t) };
    } catch (i) {
      mt.warn("Failed to load settings, using defaults:", i);
      const o = i instanceof _ ? { type: i.errorType, message: i.toUserMessage() } : { type: "UNKNOWN", message: "En ukjent feil oppstod ved lasting av innstillinger." };
      return { settings: r.defaultSettings, error: o };
    }
  }
), pr = Oe({
  name: "settings",
  initialState: vt,
  reducers: {
    setSettings: (n, r) => {
      n.settings = r.payload;
    }
  },
  extraReducers: (n) => {
    n.addCase(Ce.pending, (r) => {
      r.isLoading = !0;
    }).addCase(Ce.fulfilled, (r, t) => {
      r.isLoading = !1, r.settings = t.payload.settings, r.error = t.payload.error;
    });
  }
}), { setSettings: Zt } = pr.actions, yt = pr.reducer, wt = (n) => n.auth.settings.settings, Rt = (n) => n.auth.settings.isLoading, Et = (n) => n.auth.settings.error;
function gr(n, r, t = !1) {
  const i = Ae(), o = $(wt), p = $(Rt), d = $(Et), c = xe(!1);
  return j(() => {
    c.current || (c.current = !0, i(Ce({ settingsService: n, config: r, requireAuth: t })));
  }, [i, n, t]), { settings: o || r.defaultSettings, isLoading: p, settingsError: d };
}
const Se = x("useTokenRegistration");
function en(n, r) {
  j(() => {
    if (n) {
      Se.debug(`Registering token for ${r}`);
      try {
        be.registerToken({
          server: r,
          token: n
        }), Se.debug(`Token registered successfully for ${r}`);
      } catch (t) {
        Se.error("Failed to register token:", t);
      }
    }
  }, [n, r]);
}
function rn(n, r, t) {
  const [i] = F(() => new ar({ appId: "06bJzGl3cUQAR5lX", portalUrl: t })), [o] = F(() => new ur(n, { requireAuth: !1, portalUrl: t })), [p] = F(() => new or({ portalUrl: t }));
  j(() => {
    i.initialize();
  }, [i]), cr(i, !1);
  const { token: d } = hr(o), { settings: c, isLoading: g, settingsError: S } = gr(p, { appName: n, defaultSettings: r }, !1);
  return { token: d, settings: c, isLoading: g, settingsError: S };
}
const kt = x("useAuthenticatedApp");
function tn(n, r, t) {
  const { portalUrl: i, requiredGroup: o } = t || {}, [p] = F(() => new ar({ appId: "06bJzGl3cUQAR5lX", portalUrl: i })), [d] = F(() => new ur(n, { requireAuth: !0, portalUrl: i })), [c] = F(() => new or({ portalUrl: i })), g = xe(!1), [S, v] = F(
    o ? "checking" : "skipped"
  );
  j(() => {
    p.initialize();
  }, [p]);
  const y = cr(p, !0), { token: w } = hr(d), { settings: O, isLoading: ee, settingsError: re } = gr(c, { appName: n, defaultSettings: r }, !0);
  j(() => {
    !y.isLoading && !y.isSignedIn && !g.current && (g.current = !0, y.signIn());
  }, [y.isLoading, y.isSignedIn]), j(() => {
    if (!o || !y.isSignedIn)
      return;
    let L = !1;
    return p.isMemberOfGroup(o).then((V) => {
      L || v(V ? "member" : "not-member");
    }).catch((V) => {
      kt.error("Failed to check group membership, denying access:", V), L || v("not-member");
    }), () => {
      L = !0;
    };
  }, [p, o, y.isSignedIn]);
  const fe = !!o && S === "not-member", I = y.isSignedIn && !ee && !!w && (!o || S === "member");
  let E = null;
  return o && S === "checking" || (fe ? E = { kind: "group" } : re && (E = { kind: "settings", error: re })), {
    isSignedIn: y.isSignedIn,
    user: y.user,
    token: w,
    settings: O,
    isReady: I,
    signOut: y.signOut,
    accessError: E
  };
}
var _e = { exports: {} }, z = {};
/**
 * @license React
 * react-jsx-runtime.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var nr;
function St() {
  if (nr)
    return z;
  nr = 1;
  var n = Z, r = Symbol.for("react.element"), t = Symbol.for("react.fragment"), i = Object.prototype.hasOwnProperty, o = n.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner, p = { key: !0, ref: !0, __self: !0, __source: !0 };
  function d(c, g, S) {
    var v, y = {}, w = null, O = null;
    S !== void 0 && (w = "" + S), g.key !== void 0 && (w = "" + g.key), g.ref !== void 0 && (O = g.ref);
    for (v in g)
      i.call(g, v) && !p.hasOwnProperty(v) && (y[v] = g[v]);
    if (c && c.defaultProps)
      for (v in g = c.defaultProps, g)
        y[v] === void 0 && (y[v] = g[v]);
    return { $$typeof: r, type: c, key: w, ref: O, props: y, _owner: o.current };
  }
  return z.Fragment = t, z.jsx = d, z.jsxs = d, z;
}
var Q = {};
/**
 * @license React
 * react-jsx-runtime.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var sr;
function bt() {
  return sr || (sr = 1, process.env.NODE_ENV !== "production" && function() {
    var n = Z, r = Symbol.for("react.element"), t = Symbol.for("react.portal"), i = Symbol.for("react.fragment"), o = Symbol.for("react.strict_mode"), p = Symbol.for("react.profiler"), d = Symbol.for("react.provider"), c = Symbol.for("react.context"), g = Symbol.for("react.forward_ref"), S = Symbol.for("react.suspense"), v = Symbol.for("react.suspense_list"), y = Symbol.for("react.memo"), w = Symbol.for("react.lazy"), O = Symbol.for("react.offscreen"), ee = Symbol.iterator, re = "@@iterator";
    function fe(e) {
      if (e === null || typeof e != "object")
        return null;
      var s = ee && e[ee] || e[re];
      return typeof s == "function" ? s : null;
    }
    var I = n.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
    function E(e) {
      {
        for (var s = arguments.length, a = new Array(s > 1 ? s - 1 : 0), u = 1; u < s; u++)
          a[u - 1] = arguments[u];
        L("error", e, a);
      }
    }
    function L(e, s, a) {
      {
        var u = I.ReactDebugCurrentFrame, h = u.getStackAddendum();
        h !== "" && (s += "%s", a = a.concat([h]));
        var m = a.map(function(f) {
          return String(f);
        });
        m.unshift("Warning: " + s), Function.prototype.apply.call(console[e], console, m);
      }
    }
    var V = !1, mr = !1, vr = !1, yr = !1, wr = !1, Pe;
    Pe = Symbol.for("react.module.reference");
    function Rr(e) {
      return !!(typeof e == "string" || typeof e == "function" || e === i || e === p || wr || e === o || e === S || e === v || yr || e === O || V || mr || vr || typeof e == "object" && e !== null && (e.$$typeof === w || e.$$typeof === y || e.$$typeof === d || e.$$typeof === c || e.$$typeof === g || // This needs to include all possible module reference object
      // types supported by any Flight configuration anywhere since
      // we don't know which Flight build this will end up being used
      // with.
      e.$$typeof === Pe || e.getModuleId !== void 0));
    }
    function Er(e, s, a) {
      var u = e.displayName;
      if (u)
        return u;
      var h = s.displayName || s.name || "";
      return h !== "" ? a + "(" + h + ")" : a;
    }
    function je(e) {
      return e.displayName || "Context";
    }
    function A(e) {
      if (e == null)
        return null;
      if (typeof e.tag == "number" && E("Received an unexpected object in getComponentNameFromType(). This is likely a bug in React. Please file an issue."), typeof e == "function")
        return e.displayName || e.name || null;
      if (typeof e == "string")
        return e;
      switch (e) {
        case i:
          return "Fragment";
        case t:
          return "Portal";
        case p:
          return "Profiler";
        case o:
          return "StrictMode";
        case S:
          return "Suspense";
        case v:
          return "SuspenseList";
      }
      if (typeof e == "object")
        switch (e.$$typeof) {
          case c:
            var s = e;
            return je(s) + ".Consumer";
          case d:
            var a = e;
            return je(a._context) + ".Provider";
          case g:
            return Er(e, e.render, "ForwardRef");
          case y:
            var u = e.displayName || null;
            return u !== null ? u : A(e.type) || "Memo";
          case w: {
            var h = e, m = h._payload, f = h._init;
            try {
              return A(f(m));
            } catch {
              return null;
            }
          }
        }
      return null;
    }
    var D = Object.assign, Y = 0, $e, De, Ue, Ne, Fe, Me, qe;
    function We() {
    }
    We.__reactDisabledLog = !0;
    function kr() {
      {
        if (Y === 0) {
          $e = console.log, De = console.info, Ue = console.warn, Ne = console.error, Fe = console.group, Me = console.groupCollapsed, qe = console.groupEnd;
          var e = {
            configurable: !0,
            enumerable: !0,
            value: We,
            writable: !0
          };
          Object.defineProperties(console, {
            info: e,
            log: e,
            warn: e,
            error: e,
            group: e,
            groupCollapsed: e,
            groupEnd: e
          });
        }
        Y++;
      }
    }
    function Sr() {
      {
        if (Y--, Y === 0) {
          var e = {
            configurable: !0,
            enumerable: !0,
            writable: !0
          };
          Object.defineProperties(console, {
            log: D({}, e, {
              value: $e
            }),
            info: D({}, e, {
              value: De
            }),
            warn: D({}, e, {
              value: Ue
            }),
            error: D({}, e, {
              value: Ne
            }),
            group: D({}, e, {
              value: Fe
            }),
            groupCollapsed: D({}, e, {
              value: Me
            }),
            groupEnd: D({}, e, {
              value: qe
            })
          });
        }
        Y < 0 && E("disabledDepth fell below zero. This is a bug in React. Please file an issue.");
      }
    }
    var de = I.ReactCurrentDispatcher, he;
    function te(e, s, a) {
      {
        if (he === void 0)
          try {
            throw Error();
          } catch (h) {
            var u = h.stack.trim().match(/\n( *(at )?)/);
            he = u && u[1] || "";
          }
        return `
` + he + e;
      }
    }
    var pe = !1, ne;
    {
      var br = typeof WeakMap == "function" ? WeakMap : Map;
      ne = new br();
    }
    function Le(e, s) {
      if (!e || pe)
        return "";
      {
        var a = ne.get(e);
        if (a !== void 0)
          return a;
      }
      var u;
      pe = !0;
      var h = Error.prepareStackTrace;
      Error.prepareStackTrace = void 0;
      var m;
      m = de.current, de.current = null, kr();
      try {
        if (s) {
          var f = function() {
            throw Error();
          };
          if (Object.defineProperty(f.prototype, "props", {
            set: function() {
              throw Error();
            }
          }), typeof Reflect == "object" && Reflect.construct) {
            try {
              Reflect.construct(f, []);
            } catch (T) {
              u = T;
            }
            Reflect.construct(e, [], f);
          } else {
            try {
              f.call();
            } catch (T) {
              u = T;
            }
            e.call(f.prototype);
          }
        } else {
          try {
            throw Error();
          } catch (T) {
            u = T;
          }
          e();
        }
      } catch (T) {
        if (T && u && typeof T.stack == "string") {
          for (var l = T.stack.split(`
`), b = u.stack.split(`
`), R = l.length - 1, k = b.length - 1; R >= 1 && k >= 0 && l[R] !== b[k]; )
            k--;
          for (; R >= 1 && k >= 0; R--, k--)
            if (l[R] !== b[k]) {
              if (R !== 1 || k !== 1)
                do
                  if (R--, k--, k < 0 || l[R] !== b[k]) {
                    var C = `
` + l[R].replace(" at new ", " at ");
                    return e.displayName && C.includes("<anonymous>") && (C = C.replace("<anonymous>", e.displayName)), typeof e == "function" && ne.set(e, C), C;
                  }
                while (R >= 1 && k >= 0);
              break;
            }
        }
      } finally {
        pe = !1, de.current = m, Sr(), Error.prepareStackTrace = h;
      }
      var q = e ? e.displayName || e.name : "", U = q ? te(q) : "";
      return typeof e == "function" && ne.set(e, U), U;
    }
    function Tr(e, s, a) {
      return Le(e, !1);
    }
    function Cr(e) {
      var s = e.prototype;
      return !!(s && s.isReactComponent);
    }
    function se(e, s, a) {
      if (e == null)
        return "";
      if (typeof e == "function")
        return Le(e, Cr(e));
      if (typeof e == "string")
        return te(e);
      switch (e) {
        case S:
          return te("Suspense");
        case v:
          return te("SuspenseList");
      }
      if (typeof e == "object")
        switch (e.$$typeof) {
          case g:
            return Tr(e.render);
          case y:
            return se(e.type, s, a);
          case w: {
            var u = e, h = u._payload, m = u._init;
            try {
              return se(m(h), s, a);
            } catch {
            }
          }
        }
      return "";
    }
    var G = Object.prototype.hasOwnProperty, Ve = {}, Ye = I.ReactDebugCurrentFrame;
    function ie(e) {
      if (e) {
        var s = e._owner, a = se(e.type, e._source, s ? s.type : null);
        Ye.setExtraStackFrame(a);
      } else
        Ye.setExtraStackFrame(null);
    }
    function _r(e, s, a, u, h) {
      {
        var m = Function.call.bind(G);
        for (var f in e)
          if (m(e, f)) {
            var l = void 0;
            try {
              if (typeof e[f] != "function") {
                var b = Error((u || "React class") + ": " + a + " type `" + f + "` is invalid; it must be a function, usually from the `prop-types` package, but received `" + typeof e[f] + "`.This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.");
                throw b.name = "Invariant Violation", b;
              }
              l = e[f](s, f, u, a, null, "SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED");
            } catch (R) {
              l = R;
            }
            l && !(l instanceof Error) && (ie(h), E("%s: type specification of %s `%s` is invalid; the type checker function must return `null` or an `Error` but returned a %s. You may have forgotten to pass an argument to the type checker creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and shape all require an argument).", u || "React class", a, f, typeof l), ie(null)), l instanceof Error && !(l.message in Ve) && (Ve[l.message] = !0, ie(h), E("Failed %s type: %s", a, l.message), ie(null));
          }
      }
    }
    var xr = Array.isArray;
    function ge(e) {
      return xr(e);
    }
    function Ar(e) {
      {
        var s = typeof Symbol == "function" && Symbol.toStringTag, a = s && e[Symbol.toStringTag] || e.constructor.name || "Object";
        return a;
      }
    }
    function Or(e) {
      try {
        return Ge(e), !1;
      } catch {
        return !0;
      }
    }
    function Ge(e) {
      return "" + e;
    }
    function Be(e) {
      if (Or(e))
        return E("The provided key is an unsupported type %s. This value must be coerced to a string before before using it here.", Ar(e)), Ge(e);
    }
    var B = I.ReactCurrentOwner, Ir = {
      key: !0,
      ref: !0,
      __self: !0,
      __source: !0
    }, Ke, Je, me;
    me = {};
    function Pr(e) {
      if (G.call(e, "ref")) {
        var s = Object.getOwnPropertyDescriptor(e, "ref").get;
        if (s && s.isReactWarning)
          return !1;
      }
      return e.ref !== void 0;
    }
    function jr(e) {
      if (G.call(e, "key")) {
        var s = Object.getOwnPropertyDescriptor(e, "key").get;
        if (s && s.isReactWarning)
          return !1;
      }
      return e.key !== void 0;
    }
    function $r(e, s) {
      if (typeof e.ref == "string" && B.current && s && B.current.stateNode !== s) {
        var a = A(B.current.type);
        me[a] || (E('Component "%s" contains the string ref "%s". Support for string refs will be removed in a future major release. This case cannot be automatically converted to an arrow function. We ask you to manually fix this case by using useRef() or createRef() instead. Learn more about using refs safely here: https://reactjs.org/link/strict-mode-string-ref', A(B.current.type), e.ref), me[a] = !0);
      }
    }
    function Dr(e, s) {
      {
        var a = function() {
          Ke || (Ke = !0, E("%s: `key` is not a prop. Trying to access it will result in `undefined` being returned. If you need to access the same value within the child component, you should pass it as a different prop. (https://reactjs.org/link/special-props)", s));
        };
        a.isReactWarning = !0, Object.defineProperty(e, "key", {
          get: a,
          configurable: !0
        });
      }
    }
    function Ur(e, s) {
      {
        var a = function() {
          Je || (Je = !0, E("%s: `ref` is not a prop. Trying to access it will result in `undefined` being returned. If you need to access the same value within the child component, you should pass it as a different prop. (https://reactjs.org/link/special-props)", s));
        };
        a.isReactWarning = !0, Object.defineProperty(e, "ref", {
          get: a,
          configurable: !0
        });
      }
    }
    var Nr = function(e, s, a, u, h, m, f) {
      var l = {
        // This tag allows us to uniquely identify this as a React Element
        $$typeof: r,
        // Built-in properties that belong on the element
        type: e,
        key: s,
        ref: a,
        props: f,
        // Record the component responsible for creating this element.
        _owner: m
      };
      return l._store = {}, Object.defineProperty(l._store, "validated", {
        configurable: !1,
        enumerable: !1,
        writable: !0,
        value: !1
      }), Object.defineProperty(l, "_self", {
        configurable: !1,
        enumerable: !1,
        writable: !1,
        value: u
      }), Object.defineProperty(l, "_source", {
        configurable: !1,
        enumerable: !1,
        writable: !1,
        value: h
      }), Object.freeze && (Object.freeze(l.props), Object.freeze(l)), l;
    };
    function Fr(e, s, a, u, h) {
      {
        var m, f = {}, l = null, b = null;
        a !== void 0 && (Be(a), l = "" + a), jr(s) && (Be(s.key), l = "" + s.key), Pr(s) && (b = s.ref, $r(s, h));
        for (m in s)
          G.call(s, m) && !Ir.hasOwnProperty(m) && (f[m] = s[m]);
        if (e && e.defaultProps) {
          var R = e.defaultProps;
          for (m in R)
            f[m] === void 0 && (f[m] = R[m]);
        }
        if (l || b) {
          var k = typeof e == "function" ? e.displayName || e.name || "Unknown" : e;
          l && Dr(f, k), b && Ur(f, k);
        }
        return Nr(e, l, b, h, u, B.current, f);
      }
    }
    var ve = I.ReactCurrentOwner, ze = I.ReactDebugCurrentFrame;
    function M(e) {
      if (e) {
        var s = e._owner, a = se(e.type, e._source, s ? s.type : null);
        ze.setExtraStackFrame(a);
      } else
        ze.setExtraStackFrame(null);
    }
    var ye;
    ye = !1;
    function we(e) {
      return typeof e == "object" && e !== null && e.$$typeof === r;
    }
    function Qe() {
      {
        if (ve.current) {
          var e = A(ve.current.type);
          if (e)
            return `

Check the render method of \`` + e + "`.";
        }
        return "";
      }
    }
    function Mr(e) {
      {
        if (e !== void 0) {
          var s = e.fileName.replace(/^.*[\\\/]/, ""), a = e.lineNumber;
          return `

Check your code at ` + s + ":" + a + ".";
        }
        return "";
      }
    }
    var Xe = {};
    function qr(e) {
      {
        var s = Qe();
        if (!s) {
          var a = typeof e == "string" ? e : e.displayName || e.name;
          a && (s = `

Check the top-level render call using <` + a + ">.");
        }
        return s;
      }
    }
    function He(e, s) {
      {
        if (!e._store || e._store.validated || e.key != null)
          return;
        e._store.validated = !0;
        var a = qr(s);
        if (Xe[a])
          return;
        Xe[a] = !0;
        var u = "";
        e && e._owner && e._owner !== ve.current && (u = " It was passed a child from " + A(e._owner.type) + "."), M(e), E('Each child in a list should have a unique "key" prop.%s%s See https://reactjs.org/link/warning-keys for more information.', a, u), M(null);
      }
    }
    function Ze(e, s) {
      {
        if (typeof e != "object")
          return;
        if (ge(e))
          for (var a = 0; a < e.length; a++) {
            var u = e[a];
            we(u) && He(u, s);
          }
        else if (we(e))
          e._store && (e._store.validated = !0);
        else if (e) {
          var h = fe(e);
          if (typeof h == "function" && h !== e.entries)
            for (var m = h.call(e), f; !(f = m.next()).done; )
              we(f.value) && He(f.value, s);
        }
      }
    }
    function Wr(e) {
      {
        var s = e.type;
        if (s == null || typeof s == "string")
          return;
        var a;
        if (typeof s == "function")
          a = s.propTypes;
        else if (typeof s == "object" && (s.$$typeof === g || // Note: Memo only checks outer props here.
        // Inner props are checked in the reconciler.
        s.$$typeof === y))
          a = s.propTypes;
        else
          return;
        if (a) {
          var u = A(s);
          _r(a, e.props, "prop", u, e);
        } else if (s.PropTypes !== void 0 && !ye) {
          ye = !0;
          var h = A(s);
          E("Component %s declared `PropTypes` instead of `propTypes`. Did you misspell the property assignment?", h || "Unknown");
        }
        typeof s.getDefaultProps == "function" && !s.getDefaultProps.isReactClassApproved && E("getDefaultProps is only used on classic React.createClass definitions. Use a static property named `defaultProps` instead.");
      }
    }
    function Lr(e) {
      {
        for (var s = Object.keys(e.props), a = 0; a < s.length; a++) {
          var u = s[a];
          if (u !== "children" && u !== "key") {
            M(e), E("Invalid prop `%s` supplied to `React.Fragment`. React.Fragment can only have `key` and `children` props.", u), M(null);
            break;
          }
        }
        e.ref !== null && (M(e), E("Invalid attribute `ref` supplied to `React.Fragment`."), M(null));
      }
    }
    var er = {};
    function rr(e, s, a, u, h, m) {
      {
        var f = Rr(e);
        if (!f) {
          var l = "";
          (e === void 0 || typeof e == "object" && e !== null && Object.keys(e).length === 0) && (l += " You likely forgot to export your component from the file it's defined in, or you might have mixed up default and named imports.");
          var b = Mr(h);
          b ? l += b : l += Qe();
          var R;
          e === null ? R = "null" : ge(e) ? R = "array" : e !== void 0 && e.$$typeof === r ? (R = "<" + (A(e.type) || "Unknown") + " />", l = " Did you accidentally export a JSX literal instead of a component?") : R = typeof e, E("React.jsx: type is invalid -- expected a string (for built-in components) or a class/function (for composite components) but got: %s.%s", R, l);
        }
        var k = Fr(e, s, a, h, m);
        if (k == null)
          return k;
        if (f) {
          var C = s.children;
          if (C !== void 0)
            if (u)
              if (ge(C)) {
                for (var q = 0; q < C.length; q++)
                  Ze(C[q], e);
                Object.freeze && Object.freeze(C);
              } else
                E("React.jsx: Static children should always be an array. You are likely explicitly calling React.jsxs or React.jsxDEV. Use the Babel transform instead.");
            else
              Ze(C, e);
        }
        if (G.call(s, "key")) {
          var U = A(e), T = Object.keys(s).filter(function(Jr) {
            return Jr !== "key";
          }), Re = T.length > 0 ? "{key: someKey, " + T.join(": ..., ") + ": ...}" : "{key: someKey}";
          if (!er[U + Re]) {
            var Kr = T.length > 0 ? "{" + T.join(": ..., ") + ": ...}" : "{}";
            E(`A props object containing a "key" prop is being spread into JSX:
  let props = %s;
  <%s {...props} />
React keys must be passed directly to JSX without using spread:
  let props = %s;
  <%s key={someKey} {...props} />`, Re, U, Kr, U), er[U + Re] = !0;
          }
        }
        return e === i ? Lr(k) : Wr(k), k;
      }
    }
    function Vr(e, s, a) {
      return rr(e, s, a, !0);
    }
    function Yr(e, s, a) {
      return rr(e, s, a, !1);
    }
    var Gr = Yr, Br = Vr;
    Q.Fragment = i, Q.jsx = Gr, Q.jsxs = Br;
  }()), Q;
}
process.env.NODE_ENV === "production" ? _e.exports = St() : _e.exports = bt();
var X = _e.exports;
const Tt = x("SettingsErrorBoundary");
class nn extends Zr {
  constructor(r) {
    super(r), this.handleRetry = () => {
      this.setState({ error: void 0 }), this.props.onRetry ? this.props.onRetry() : window.location.reload();
    }, this.state = { error: void 0 };
  }
  static getDerivedStateFromError(r) {
    return r instanceof _ ? { error: r } : { error: void 0 };
  }
  componentDidCatch(r, t) {
    r instanceof _ && Tt.error("Settings error:", r, t);
  }
  render() {
    return this.state.error ? /* @__PURE__ */ X.jsx("div", { style: { padding: "2rem", maxWidth: "600px", margin: "0 auto" }, children: /* @__PURE__ */ X.jsxs(rt, { open: !0, kind: "danger", autoClose: !1, children: [
      /* @__PURE__ */ X.jsx("div", { slot: "title", children: "Kunne ikke laste innstillinger" }),
      /* @__PURE__ */ X.jsx("div", { slot: "message", children: this.state.error.toUserMessage() }),
      /* @__PURE__ */ X.jsx(tt, { slot: "actions-end", onClick: this.handleRetry, children: "Prøv igjen" })
    ] }) }) : this.props.children;
  }
}
function sn() {
  const [n, r] = Z.useState(), t = Z.useCallback((o) => {
    o instanceof _ && r(o);
  }, []), i = Z.useCallback(() => {
    r(void 0);
  }, []);
  return { error: n, handleError: t, clearError: i };
}
const an = et({
  auth: at,
  token: dt,
  settings: yt
});
export {
  ar as AuthService,
  ur as GdoTokenProvider,
  Ft as PortalItemService,
  _ as SettingsError,
  nn as SettingsErrorBoundary,
  or as SettingsService,
  an as authCombinedReducer,
  at as authReducer,
  st as buildSettingsQueryString,
  nt as buildTokenServiceQueryString,
  oe as checkSignInStatus,
  qt as clearAuth,
  Gt as clearToken,
  Ie as getCustomerDomain,
  ce as getToken,
  Ce as loadSettings,
  ke as refreshToken,
  ue as requireSignIn,
  Qt as selectApplicationRoles,
  Wt as selectAuthError,
  lt as selectAuthStatus,
  Ht as selectGdoCustomerName,
  zt as selectGdoToken,
  Xt as selectGdoUsername,
  Lt as selectIsAuthRequired,
  ot as selectIsSignedIn,
  Kt as selectIsTokenExpired,
  pt as selectIsTokenRefreshing,
  wt as selectSettings,
  Et as selectSettingsError,
  Rt as selectSettingsLoading,
  ht as selectToken,
  Jt as selectTokenError,
  Bt as selectTokenExpiration,
  ut as selectUser,
  Yt as setAppName,
  Mt as setIsAuthRequired,
  Zt as setSettings,
  Vt as setToken,
  ft as setTokenMetadata,
  yt as settingsReducer,
  le as signOut,
  dt as tokenReducer,
  cr as useAuth,
  tn as useAuthenticatedApp,
  rn as usePublicApp,
  gr as useSettings,
  sn as useSettingsErrorHandler,
  hr as useTokenProvider,
  en as useTokenRegistration
};
//# sourceMappingURL=index.mjs.map
